const REDUCED = '(prefers-reduced-motion: reduce)';
const TAU = Math.PI * 2;

/**
 * Mango leaves drifting down from the hero grove.
 *
 * The previous petal version looked mechanical for five reasons, each
 * addressed here: constant fall speed, constant spin, a single sine for
 * sway, no flutter, and no shared air. Of these the flutter matters most —
 * a real leaf turns edge-on as it falls, which is a sign flip on scaleX.
 */
class AzLeaves extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia(REDUCED).matches) return;
    if (this.dataset.mobile === 'false' && window.matchMedia('(max-width: 749px)').matches) return;

    this.count = Number(this.dataset.count) || 16;
    this.minSize = Number(this.dataset.minSize) || 12;
    this.maxSize = Number(this.dataset.maxSize) || 26;
    this.fall = Number(this.dataset.fall) || 26;
    this.greenShare = Number(this.dataset.greenShare ?? 0.6);
    this.greens = (this.dataset.greens || '').split(',').filter(Boolean);
    this.flush = this.dataset.flush || this.greens[0];
    if (!this.greens.length) return;

    this.leaves = [];
    this.zones = [];
    this.lastTime = 0;
    this.running = false;

    this.#measureZones();
    this.#build();

    this.tick = this.#tick.bind(this);
    this.onResize = this.#measureZones.bind(this);
    this.onVisibility = this.#onVisibility.bind(this);
    window.addEventListener('resize', this.onResize, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);

    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  #rand(a, b) {
    return a + Math.random() * (b - a);
  }

  /** Spawn across the top of the host box. */
  #measureZones() {
    this.zones = [{ x0: -2, x1: 102, y0: -22, y1: -6 }];
  }

  #build() {
    const frag = document.createDocumentFragment();
    for (let i = 0; i < this.count; i++) {
      const el = document.createElement('span');
      el.className = 'az-leaves__leaf';
      const green = Math.random() < this.greenShare;
      const sprite = green
        ? this.greens[(Math.random() * this.greens.length) | 0]
        : this.flush;
      const img = document.createElement('img');
      img.src = sprite;
      img.alt = '';
      img.decoding = 'async';
      el.appendChild(img);
      frag.appendChild(el);
      this.leaves.push(this.#seed(el, true));
    }
    this.appendChild(frag);
  }

  #seed(el, initial) {
    const size = this.#rand(this.minSize, this.maxSize);
    const depth = (size - this.minSize) / Math.max(1, this.maxSize - this.minSize);
    const zone = this.zones[(Math.random() * this.zones.length) | 0];

    return {
      el,
      size,
      x: this.#rand(zone.x0, zone.x1),
      y: initial ? this.#rand(-10, 105) : this.#rand(zone.y0, zone.y1),
      // Fall accelerates toward a size-dependent terminal velocity rather than
      // running at a fixed speed.
      vy: 0,
      terminal: this.fall * (0.5 + depth * 0.85),
      drag: this.#rand(0.6, 1.3),
      // Two oscillators at incommensurate frequencies: the path never repeats.
      a1: this.#rand(1.6, 4.4),
      f1: this.#rand(0.16, 0.34),
      p1: this.#rand(0, TAU),
      a2: this.#rand(0.5, 1.8),
      f2: this.#rand(0.43, 0.83),
      p2: this.#rand(0, TAU),
      // Flutter: the edge-on flip. This is what sells it as a leaf.
      ff: this.#rand(0.5, 1.35),
      fp: this.#rand(0, TAU),
      // Most leaves flutter; a few tumble end over end.
      tumble: Math.random() < 0.22 ? this.#rand(-52, 52) : 0,
      baseRot: this.#rand(0, 360),
      gustGain: this.#rand(0.6, 1.5),
      opacity: 0.3 + depth * 0.45,
    };
  }

  #onVisibility() {
    if (document.hidden) this.#stop();
    else this.#start();
  }

  #start() {
    if (this.running) return;
    this.running = true;
    this.lastTime = 0;
    this.raf = requestAnimationFrame(this.tick);
  }

  #stop() {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  #tick(now) {
    if (!this.running) return;

    const t = now / 1000;
    const dt = this.lastTime ? Math.min(0.05, t - this.lastTime) : 0.016;
    this.lastTime = t;

    // One slow body of air every leaf sits in, so they drift together.
    const gust = Math.sin(t * 0.13) * 0.62 + Math.sin(t * 0.071 + 1.7) * 0.38;

    // Transform percentages resolve against the element's own box, not the
    // container — so positions are converted to pixels against the host.
    const hostW = this.clientWidth || window.innerWidth;
    const hostH = this.clientHeight || window.innerHeight;

    for (const p of this.leaves) {
      // approach terminal velocity instead of holding a constant speed
      p.vy += (p.terminal - p.vy) * p.drag * dt;
      p.y += ((p.vy * dt) / hostH) * 100;

      if (p.y > 116) Object.assign(p, this.#seed(p.el, false));
      else if (p.y < -40) p.y = 114;

      const s1 = Math.sin(t * p.f1 * TAU + p.p1);
      const s2 = Math.sin(t * p.f2 * TAU + p.p2);
      const sway = p.a1 * s1 + p.a2 * s2;

      // sway velocity: the leaf leans into the direction it is moving
      const swayVel =
        p.a1 * p.f1 * Math.cos(t * p.f1 * TAU + p.p1) +
        p.a2 * p.f2 * Math.cos(t * p.f2 * TAU + p.p2);

      const x = p.x + sway + gust * p.gustGain * 2.4;
      const flip = Math.cos(t * p.ff * TAU + p.fp);
      const rot = p.baseRot + swayVel * 46 + (p.tumble ? p.tumble * t : 0);

      p.el.style.cssText =
        `width:${p.size}px;height:${p.size * 2.3}px;opacity:${p.opacity};` +
        `transform:translate3d(${(x / 100) * hostW}px, ${(p.y / 100) * hostH}px, 0) ` +
        `rotate(${rot}deg) scaleX(${flip})`;
    }

    this.raf = requestAnimationFrame(this.tick);
  }
}

if (!customElements.get('az-leaves')) customElements.define('az-leaves', AzLeaves);
